import React from 'react';
import { Chip, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import LockIcon from '@mui/icons-material/Lock';

export type FProcessStage = {
  code: string;
  label: string;
  status?: 'done' | 'active' | 'pending' | 'locked' | 'warning' | 'error';
  onClick?: () => void;
};

type Props = { title?: string; stages: FProcessStage[]; activeCode?: string };

class FProcessStageBar extends React.PureComponent<Props> {
  color(status?: string): any {
    if (status === 'done') return 'success';
    if (status === 'active') return 'primary';
    if (status === 'warning') return 'warning';
    if (status === 'error') return 'error';
    return 'default';
  }

  icon(status?: string) {
    if (status === 'done') return <CheckCircleIcon />;
    if (status === 'active') return <RadioButtonCheckedIcon />;
    if (status === 'locked') return <LockIcon />;
    return undefined;
  }

  progress() {
    const stages = this.props.stages || [];
    if (!stages.length) return 0;
    const done = stages.filter((x) => x.status === 'done').length;
    const active = stages.some((x) => x.status === 'active') ? 0.5 : 0;
    return Math.min(100, ((done + active) / stages.length) * 100);
  }

  render() {
    const stages = this.props.stages || [];

    return (
      <Paper elevation={1} sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{this.props.title || 'Process Stage'}</Typography>
            <Typography variant="caption" color="text.secondary">{Math.round(this.progress())}% complete</Typography>
          </Stack>

          <LinearProgress variant="determinate" value={this.progress()} sx={{ height: 7, borderRadius: 5 }} />

          <Stack direction="row" spacing={1} flexWrap="wrap">
            {stages.map((stage) => {
              const status = stage.status || (stage.code === this.props.activeCode ? 'active' : 'pending');
              return (
                <Chip
                  key={stage.code}
                  label={stage.label}
                  color={this.color(status)}
                  icon={this.icon(status)}
                  variant={status === 'active' || status === 'done' ? 'filled' : 'outlined'}
                  onClick={stage.onClick}
                  sx={{ cursor: stage.onClick ? 'pointer' : 'default' }}
                />
              );
            })}
          </Stack>
        </Stack>
      </Paper>
    );
  }
}

export default FProcessStageBar;
